import { EnumOptionsList, SourceTypeEnum } from "@/types/common";
import { ChangeEnum, ConfirmedEnum, CountingStateEnum, OperateEnum, OperateEnum2, OrderEnum } from "@/types/inventory";
import { ProductStoreRequireCodeEnum } from "@/types/product";
import { useTranslation } from "react-i18next";

const useSourceType = (addVn = false) => {
  const { t } = useTranslation();

  const SourceTypeList: EnumOptionsList<SourceTypeEnum>[] = [
    {
      key: SourceTypeEnum.PURCHASE,
      value: SourceTypeEnum.PURCHASE,
      color: "#1890ff",
      label: addVn ? `${t("common.po", { lng: "zh" })}/${t("common.po", { lng: "vn" })}` : t("common.po"),
    },
    {
      key: SourceTypeEnum.PURCHASE_ITEM,
      value: SourceTypeEnum.PURCHASE_ITEM,
      color: "#1890ff",
      label: addVn ? `${t("common.poi", { lng: "zh" })}/${t("common.poi", { lng: "vn" })}` : t("common.poi"),
    },
    {
      key: SourceTypeEnum.SALE,
      value: SourceTypeEnum.SALE,
      color: "#52c41a",
      label: addVn ? `${t("common.so", { lng: "zh" })}/${t("common.so", { lng: "vn" })}` : t("common.so"),
    },
    {
      key: SourceTypeEnum.SALE_ITEM,
      value: SourceTypeEnum.SALE_ITEM,
      color: "#52c41a",
      label: addVn ? `${t("common.soi", { lng: "zh" })}/${t("common.soi", { lng: "vn" })}` : t("common.soi"),
    },
    {
      key: SourceTypeEnum.IN_STOCK,
      value: SourceTypeEnum.IN_STOCK,
      color: "#722ed1",
      label: addVn ? `${t("common.io", { lng: "zh" })}/${t("common.io", { lng: "vn" })}` : t("common.io"),
    },
    {
      key: SourceTypeEnum.IN_STOCK_ITEM,
      value: SourceTypeEnum.IN_STOCK_ITEM,
      color: "#722ed1",
      label: addVn ? `${t("common.ioi", { lng: "zh" })}/${t("common.ioi", { lng: "vn" })}` : t("common.ioi"),
    },
    {
      key: SourceTypeEnum.OUT_STOCK,
      value: SourceTypeEnum.OUT_STOCK,
      color: "#fa8c16",
      label: addVn ? `${t("common.oo", { lng: "zh" })}/${t("common.oo", { lng: "vn" })}` : t("common.oo"),
    },
    {
      key: SourceTypeEnum.OUT_STOCK_ITEM,
      value: SourceTypeEnum.OUT_STOCK_ITEM,
      color: "#fa8c16",
      label: addVn ? `${t("common.ooi", { lng: "zh" })}/${t("common.ooi", { lng: "vn" })}` : t("common.ooi"),
    },
    {
      key: SourceTypeEnum.STOCKTAKING,
      value: SourceTypeEnum.STOCKTAKING,
      color: "#13c2c2",
      label: addVn ? `${t("common.co", { lng: "zh" })}/${t("common.co", { lng: "vn" })}` : t("common.co"),
    },
    {
      key: SourceTypeEnum.STOCKTAKING_ITEM,
      value: SourceTypeEnum.STOCKTAKING_ITEM,
      color: "#13c2c2",
      label: addVn ? `${t("common.coi", { lng: "zh" })}/${t("common.coi", { lng: "vn" })}` : t("common.coi"),
    },
    {
      key: SourceTypeEnum.RECEIVABLE,
      value: SourceTypeEnum.RECEIVABLE,
      color: "#eb2f96",
      label: addVn ? `${t("common.act", { lng: "zh" })}/${t("common.act", { lng: "vn" })}` : t("common.act"),
    },
    {
      key: SourceTypeEnum.RECEIVABLE_ITEM,
      value: SourceTypeEnum.RECEIVABLE_ITEM,
      color: "#eb2f96",
      label: addVn ? `${t("common.acti", { lng: "zh" })}/${t("common.acti", { lng: "vn" })}` : t("common.acti"),
    },
    {
      key: SourceTypeEnum.MEET,
      value: SourceTypeEnum.MEET,
      color: "#fadb14",
      label: addVn ? `${t("common.pay", { lng: "zh" })}/${t("common.pay", { lng: "vn" })}` : t("common.pay"),
    },
    {
      key: SourceTypeEnum.MEET_ITEM,
      value: SourceTypeEnum.MEET_ITEM,
      color: "#fadb14",
      label: addVn ? `${t("common.payi", { lng: "zh" })}/${t("common.payi", { lng: "vn" })}` : t("common.payi"),
    },
    {
      key: SourceTypeEnum.REQUIREMENT,
      value: SourceTypeEnum.REQUIREMENT,
      color: "#a0d911",
      label: addVn ? `${t("common.req", { lng: "zh" })}/${t("common.req", { lng: "vn" })}` : t("common.req"),
    },
    {
      key: SourceTypeEnum.REQUIREMENT_ITEM,
      value: SourceTypeEnum.REQUIREMENT_ITEM,
      color: "#a0d911",
      label: addVn ? `${t("common.reqi", { lng: "zh" })}/${t("common.reqi", { lng: "vn" })}` : t("common.reqi"),
    },
  ];

  const opTypes = [
    { value: OperateEnum.PENDING, label: t('inventory.inv'), color: 'gold' },
    { value: OperateEnum.TRANSACTION, label: t('inventory.trade'), color: 'purple' },
    { value: OperateEnum.DISPATCH, label: t('inventory.transfer'), color: 'cyan' },
    { value: OperateEnum.TEMPORARY, label: t('inventory.temporary'), color: 'magenta' },
    { value: OperateEnum.PICKING, label: t('warehouse.sourcesPick'), color: 'orange' },
    { value: OperateEnum.IN_RETURN, label: t('warehouse.sourcesInReturn'), color: 'volcano' },
    { value: OperateEnum.OUT_RETURN, label: t('warehouse.sourcesOutReturn'), color: 'green' },
    { value: OperateEnum.PROD_IN, label: t('warehouse.proIn'), color: 'geekblue' },
    { value: OperateEnum.MATERIAL_OVER_ISSUE, label: t('reimburse.materialOverIssue'), color: 'red' },
  ];

  const opTypes2 = [
    { value: OperateEnum2.SURPLUS, label: t('warehouse.surplus'), color: 'blue' },
    { value: OperateEnum2.DEFECTIVE, label: t('warehouse.defective'), color: 'red' },
    { value: OperateEnum2.REFURBISHED, label: t('warehouse.refurbished'), color: 'gold' },
    { value: OperateEnum2.REOUTRECVD, label: t('warehouse.reoutrecvdpicking'), color: 'geekblue' },
  ];

  const zhVnOpTypes2 = [
    { value: OperateEnum2.SURPLUS, label: `${t('warehouse.surplus', { lng: "zh" })}/${t('warehouse.surplus', { lng: "vn" })}`, color: 'blue' },
    { value: OperateEnum2.DEFECTIVE, label: `${t('warehouse.defective', { lng: "zh" })}/${t('warehouse.defective', { lng: "vn" })}`, color: 'red' },
    { value: OperateEnum2.REFURBISHED, label: `${t('warehouse.refurbished', { lng: "zh" })}/${t('warehouse.refurbished', { lng: "vn" })}`, color: 'gold' },
    { value: OperateEnum2.REOUTRECVD, label: `${t('warehouse.reoutrecvdpicking', { lng: "zh" })}/${t('warehouse.reoutrecvdpicking', { lng: "vn" })}`, color: 'geekblue' },
  ];

  const changeTypes = [
    { value: ChangeEnum.IN, label: t('inventory.inbound'), color: 'blue' },
    { value: ChangeEnum.OUT, label: t('inventory.outbound'), color: 'red' },
    { value: ChangeEnum.PRE_IN, label: `${t('purchase.delivery')}/${t('purchase.preInventory')}`, color: 'blue' },
    { value: ChangeEnum.PRE_OUT, label: t('inventory.perOutbound'), color: 'red' },
  ];

  const zhVnChangeTypes = [
    { value: ChangeEnum.IN, label: `${t('inventory.inbound', { lng: "zh" })}/${t('inventory.inbound', { lng: "vn" })}`, color: 'blue' },
    { value: ChangeEnum.OUT, label: `${t('inventory.outbound', { lng: "zh" })}/${t('inventory.outbound', { lng: "vn" })}`, color: 'red' },
    { value: ChangeEnum.PRE_IN, label: `${t('purchase.preInventory', { lng: "zh" })}/${t('purchase.delivery', { lng: "zh" })}/${t('purchase.preInventory', { lng: "vn" })}/${t('purchase.delivery', { lng: "vn" })}`, color: 'blue' },
    { value: ChangeEnum.PRE_OUT, label: `${t('inventory.perOutbound', { lng: "zh" })}/${t('inventory.perOutbound', { lng: "vn" })}`, color: 'red' },
  ];

  const orderTypes = [
    { value: OrderEnum.SCAN, label: t('inventory.scanOrder'), color: 'geekblue' },
    { value: OrderEnum.COUNT, label: t('inventory.inputOrder'), color: 'green' }
  ];

  const confirmTypes = [
    { value: ConfirmedEnum.NOTCONFIRM, label: t('inventory.beConfirmed'), color: 'red' },
    { value: ConfirmedEnum.CONFIRM, label: t('inventory.confirmed'), color: 'blue' }
  ];

  const countingStatus = [
    { value: CountingStateEnum.PENDING, label: t('inventory.invInProgress'), color: 'red' },
    { value: CountingStateEnum.COUNTING, label: t('inventory.genertDifferences'), color: 'blue' },
    { value: CountingStateEnum.IN, label: t('inventory.cpletDifIn'), color: 'gold' },
    { value: CountingStateEnum.OUT, label: t('inventory.cpletDifOut'), color: 'magenta' },
    { value: CountingStateEnum.DONE, label: t('inventory.cplet'), color: 'green' }
  ];

  const productStoreCodeList = [
    { value: ProductStoreRequireCodeEnum.HIGH_HUMIDITY, label: t('product.hh'), color: 'red' },
    { value: ProductStoreRequireCodeEnum.HIGH_TEMP, label: t('product.ht'), color: 'blue' },
    { value: ProductStoreRequireCodeEnum.VACUUM, label: t('product.va'), color: 'magenta' },
    { value: ProductStoreRequireCodeEnum.OTHER, label: t('product.ot'), color: 'gold' },
  ];

  const getSourceType = (value: SourceTypeEnum | null) => {
    if (value === null) return
    return SourceTypeList.find(i => i.value === value)
  };

  const getOpType = (value: OperateEnum | null) => {
    if (value === null) return
    return opTypes.find(i => i.value === value)
  };

  const getOpType2 = (value: OperateEnum2 | null) => {
    if (value === null) return
    return opTypes2.find(i => i.value === value)
  };

  const getZhVnOpType2 = (value: OperateEnum2 | null) => {
    if (value === null) return
    return zhVnOpTypes2.find(i => i.value === value)
  };

  const getChangeType = (value: ChangeEnum | null) => {
    if (value === null) return
    return changeTypes.find(i => i.value === value)
  };

  const getZhVnChangeType = (value: ChangeEnum | null) => {
    if (value === null) return
    return zhVnChangeTypes.find(i => i.value === value)
  };

  const getOrderType = (value: OrderEnum | null) => {
    if (value === null) return
    return orderTypes.find(i => i.value === value)
  };

  const getConfirmType = (value: ConfirmedEnum | null) => {
    if (value === null) return
    return confirmTypes.find(i => i.value === value)
  };

  const getCountingStatus = (value: CountingStateEnum | null) => {
    if (value === null) return
    return countingStatus.find(i => i.value === value)
  };

  const getProductStore = (value: ProductStoreRequireCodeEnum | null) => {
    if (value === null) return;
    return productStoreCodeList.find(i => i.value === value)
  }

  return {
    SourceTypeList,
    getSourceType,
    opTypes,
    opTypes2,
    zhVnOpTypes2,
    changeTypes,
    zhVnChangeTypes,
    orderTypes,
    confirmTypes,
    countingStatus,
    productStoreCodeList,
    getOpType,
    getOpType2,
    getZhVnOpType2,
    getChangeType,
    getZhVnChangeType,
    getOrderType,
    getConfirmType,
    getCountingStatus,
    getProductStore
  }
};

export default useSourceType;